import React, { useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Loader2 } from 'lucide-react';
import HpQuiz from './HpQuiz';
import GuidedHypnosisPlayer from './GuidedHypnosisPlayer';
import { usePet } from '../contexts/PetContext';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const BREAK_SEC = 60;
const fmt = (s) => `${Math.floor(s / 60)}:${String(s % 60).padStart(2, '0')}`;
const post = (url, body = {}) => axios.post(`${API}/hp-daily/exam${url}`, body, { withCredentials: true, timeout: 120000 }).then((r) => r.data);

function useCountdown(seconds, running) {
  const [left, setLeft] = useState(seconds);
  useEffect(() => { setLeft(seconds); }, [seconds]);
  useEffect(() => {
    if (!running) return undefined;
    const t = setInterval(() => setLeft((l) => Math.max(0, l - 1)), 1000);
    return () => clearInterval(t);
  }, [running, seconds]);
  return left;
}

function ExamFinal({ exam, onClose }) {
  return (
    <div className="text-center space-y-2" data-testid="hp-exam-final">
      <div className="text-5xl">🎓🐹</div>
      <div className="font-['Fredoka'] text-3xl font-bold" data-testid="hp-exam-total">{exam.total_score}/{exam.max_score}</div>
      <div className="text-sm">Uppskattad normering: <b data-testid="hp-exam-normalized">≈ {exam.normalized?.toFixed(2)}</b> <span className="opacity-60">(skala 0–2, bara en fingervisning)</span></div>
      <div className="grid grid-cols-4 gap-2">
        {exam.sections.map((s) => (
          <div key={s.part.id} className="bg-white rounded-xl p-2 text-xs" data-testid={`hp-exam-final-${s.part.id}`}>
            <div className="font-black">{s.part.emoji} {s.part.name}</div><div>{s.score}/{s.total}{s.late ? ' ⏰' : ''}</div>
          </div>
        ))}
      </div>
      <p className="text-xs opacity-70">+{exam.xp} XP till husdjuret. Du klarade en hel provdag, det är stort! 💛</p>
      <button onClick={onClose} className="neo-btn px-4 py-2 bg-white font-bold text-sm" data-testid="hp-exam-close">Stäng</button>
    </div>
  );
}

export default function HpExamDay({ onDone }) {
  const [exam, setExam] = useState(null);
  const [phase, setPhase] = useState('idle');
  const [i, setI] = useState(0);
  const [calm, setCalm] = useState(null);
  const [busy, setBusy] = useState('');
  const prepRef = useRef({});
  const { refreshPet } = usePet();

  useEffect(() => {
    axios.get(`${API}/hp-daily/exam/current`, { withCredentials: true }).then((r) => {
      if (r.data.exam) { setExam(r.data.exam); setPhase('resume'); }
    }).catch(() => {});
  }, []);

  const sec = exam?.sections[i];
  const quizLeft = useCountdown(sec?.remaining_sec ?? sec?.time_limit_sec ?? 0, phase === 'quiz');
  const breakLeft = useCountdown(BREAK_SEC, phase === 'break');
  useEffect(() => { if (phase === 'break' && breakLeft === 0) goSection(i + 1); }, [breakLeft, phase]); // eslint-disable-line react-hooks/exhaustive-deps

  const prepare = (idx, id) => {
    if (!prepRef.current[idx]) {
      prepRef.current[idx] = post(`/${id}/section/${idx}/prepare`).then((e) => { setExam(e); return e; })
        .catch((err) => { prepRef.current[idx] = null; throw err; });
    }
    return prepRef.current[idx];
  };
  const goSection = async (idx, ex = exam) => {
    setI(idx);
    const s = ex.sections[idx];
    if (s.status === 'active') { setPhase('ready'); return; }
    prepare(idx, ex.id).catch(() => toast.error('Hampus tappade frågorna, prova igen 🐹'));
    try {
      const { data } = await axios.post(`${API}/hypnos/guided/exam-calm`, {}, { withCredentials: true });
      setCalm(data); setPhase('calm');
    } catch { setPhase('ready'); }
  };
  const start = async () => {
    setBusy('start');
    try {
      const e = exam && !exam.finished ? exam : await post('/start');
      setExam(e);
      const next = e.sections.findIndex((s) => s.status !== 'done');
      await goSection(next < 0 ? 0 : next, e);
    } catch { toast.error('Kunde inte starta provdagen'); }
    setBusy('');
  };
  const begin = async () => {
    setBusy('begin');
    try {
      await prepare(i, exam.id);
      setExam(await post(`/${exam.id}/section/${i}/begin`));
      setPhase('quiz');
    } catch (e) { toast.error(e?.response?.data?.detail || 'Frågorna är inte klara än, vänta en sekund'); }
    setBusy('');
  };
  const submit = async (answers) => {
    setBusy('submit');
    try {
      const e = await post(`/${exam.id}/section/${i}/submit`, { answers });
      setExam(e);
      if (e.finished) { setPhase('final'); refreshPet?.(); onDone?.(); }
      else setPhase('result');
    } catch (err) { toast.error(err?.response?.data?.detail || 'Kunde inte lämna in'); }
    setBusy('');
  };
  const close = () => {
    if (exam && !exam.finished) { setPhase('resume'); setCalm(null); }
    else { setPhase('idle'); setExam(null); prepRef.current = {}; }
  };

  if (phase === 'idle' || phase === 'resume') {
    return (
      <section className="neo-card p-4 bg-gradient-to-br from-[#E8F0FF] to-[#F5EEFF] space-y-2" data-testid="hp-exam-launcher">
        <h4 className="font-['Fredoka'] font-bold text-lg">⏱️ Mini-provdag</h4>
        <p className="text-xs opacity-70">4 delar (ORD, LÄS, XYZ, KVA) × 10 frågor med riktig tidtagning, 2 min lugn före varje del, en minuts paus emellan och totalpoäng. Cirka 1 timme.</p>
        <button onClick={start} disabled={!!busy} className="neo-btn px-4 py-2 bg-[#C8B6FF] font-bold text-sm" data-testid="hp-exam-start">
          {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : phase === 'resume' ? '▶ Fortsätt provdagen' : '▶ Starta provdagen'}
        </button>
      </section>
    );
  }

  return (
    <section className="neo-card p-4 bg-[#F8F5FF] space-y-3" data-testid="hp-exam">
      <div className="flex items-center justify-between gap-2">
        <div className="flex gap-1">
          {exam.sections.map((s, k) => (
            <span key={k} className={`text-[10px] font-black px-2 py-1 rounded-full border-2 border-black ${s.status === 'done' ? 'bg-[#B9FBC0]' : k === i ? 'bg-[#FFE8A3]' : 'bg-white'}`}>{s.part.emoji} {s.part.name}</span>
          ))}
        </div>
        {phase === 'quiz' && <span className={`font-mono font-black text-lg ${quizLeft < 60 ? 'text-[#C24672]' : ''}`} data-testid="hp-exam-timer">{fmt(quizLeft)}</span>}
      </div>
      {phase === 'ready' && (
        <div className="text-center space-y-2" data-testid="hp-exam-ready">
          <div className="font-['Fredoka'] text-xl font-bold">Del {i + 1}/4: {sec.part.emoji} {sec.part.fullname}</div>
          <p className="text-sm opacity-70">{sec.total} frågor · {Math.round(sec.time_limit_sec / 60)} minuter</p>
          <button onClick={begin} disabled={!!busy} className="neo-btn px-5 py-2.5 bg-[#F5B841] font-bold" data-testid="hp-exam-begin">
            {busy ? <><Loader2 className="w-4 h-4 animate-spin inline" /> Hampus lägger fram frågorna…</> : '⏱️ Starta tiden'}
          </button>
        </div>
      )}
      {phase === 'quiz' && <HpQuiz quiz={sec} onSubmit={submit} busy={busy === 'submit'} prefix="hp-exam" timeUp={quizLeft === 0} />}
      {phase === 'result' && (
        <div className="text-center space-y-2" data-testid="hp-exam-section-result">
          <div className="font-['Fredoka'] text-2xl font-bold">{sec.part.name}: {sec.score}/{sec.total}</div>
          <button onClick={() => setPhase('break')} className="neo-btn px-4 py-2 bg-[#B9FBC0] font-bold text-sm" data-testid="hp-exam-to-break">Ta en paus ☕</button>
        </div>
      )}
      {phase === 'break' && (
        <div className="text-center space-y-2" data-testid="hp-exam-break">
          <div className="text-4xl">☕🐹</div>
          <p className="text-sm">Paus! Sträck på dig, drick lite vatten och titta ut genom fönstret.</p>
          <div className="font-mono text-2xl font-black" data-testid="hp-exam-break-timer">{fmt(breakLeft)}</div>
          <button onClick={() => goSection(i + 1)} className="text-xs underline opacity-70" data-testid="hp-exam-skip-break">Jag är redo, fortsätt</button>
        </div>
      )}
      {phase === 'final' && <ExamFinal exam={exam} onClose={close} />}
      {phase !== 'final' && <button onClick={close} className="text-[11px] underline opacity-50" data-testid="hp-exam-pause">Pausa provdagen (fortsätt senare)</button>}
      {phase === 'calm' && calm && (
        <GuidedHypnosisPlayer session={calm} voiceOn onClose={() => { setCalm(null); setPhase('ready'); }} onFinish={() => { setCalm(null); setPhase('ready'); }} />
      )}
    </section>
  );
}
